<template>
  <div v-if="score !== null" class="quality-indicator" v-show="isVideoMeet || score < MosQuality.GOOD" @click="toggleStats" :class="{ 'pulsing-border': showPulsingBorder, 'is-video-meet': isVideoMeet }">
    <div class="signal-bars">
      <div class="bar" :class="{ active: score >= MosQuality.POOR }"></div>
      <div class="bar" :class="{ active: score >= MosQuality.FAIR }"></div>
      <div class="bar" :class="{ active: score >= MosQuality.GOOD }"></div>
      <div class="bar" :class="{ active: score >= MosQuality.EXCELLENT }"></div>
    </div>

    <teleport to="body">
      <b-modal v-model="statsModalOpen" :destroy-on-hide="false" class="webrtc-stats-modal">
        <ld-modal-content title="Connection Quality">
          <div v-if="networkStatus">
            <div class="connection-status mb-4">
              <div class="title">Your overall connection is <span :style="{ color: networkColorStatus }">{{ networkTextStatus }}</span></div>
              <div class="subtitle">{{ connectionSubtitle }}</div>
            </div>

            <div class="network-status mb-4">
              <div class="metrics-grid">
                <div v-for="(metric, key) in metricsDefinitions" :key="key" class="metric-item">
                  <div class="metric-info">
                    <div class="label">{{ metric.label }}</div>
                    <div class="description">{{ metric.description }}</div>
                  </div>
                  <div class="metric-value">
                    <div class="status" :style="{ color: getMetricColor(networkStatus[key].status) }">{{ networkStatus[key].status }}</div>
                    <div class="value">{{ networkStatus[key].value }}{{ metric.unit }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="advanced-details">
              <a href="https://speed.cloudflare.com/" target="_blank" rel="noopener noreferrer" class="mb-4 is-block">Test Internet Speed</a>
              <details>
                <summary>Advanced Details</summary>
                <div class="mt-4">
                  <p class="mb-1"><strong>Score:</strong> {{ score.toFixed(2) }}</p>
                  <details class="mb-2">
                    <summary>Raw Stats</summary>
                    <pre class="json-data">{{ JSON.stringify(stats, null, 2) }}</pre>
                  </details>
                  <details>
                    <summary>Issues</summary>
                    <pre class="json-data">{{ JSON.stringify(issues, null, 2) }}</pre>
                  </details>
                </div>
              </details>
            </div>
          </div>

          <template #footer>
            <div class="is-flex is-justify-content-center">
              <button class="button" @click="statsModalOpen = false">Close</button>
            </div>
          </template>
        </ld-modal-content>
      </b-modal>
    </teleport>
  </div>
</template>

<script>
import { MosQuality } from 'webrtc-issue-detector'
import LdModalContent from "@/components/LdModalContent"

export default {
  name: "QualityIndicator",
  components: { LdModalContent },
  props: {
    isVideoMeet: {
      type: Boolean,
      default: false
    },
    wifi: {
      type: Object,
      default: () => ({
        state: '',
        strength: 0
      })
    },
    isWifiNetworkInterface: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      score: null,
      stats: {},
      issues: [],
      showPulsingBorder: false,
      MosQuality,
      statsModalOpen: false,
      networkStatus: null,
      issuesClearTimer: null
    }
  },
  computed: {
    qualityLevel() {
      if (this.score >= MosQuality.EXCELLENT) return 'excellent'
      if (this.score >= MosQuality.GOOD) return 'good'
      if (this.score >= MosQuality.FAIR) return 'fair'
      return 'poor'
    },
    networkTextStatus() {
      return this.$helpers.capitalize(this.qualityLevel)
    },
    networkColorStatus() {
      switch (this.qualityLevel) {
        case 'excellent':
        case 'good':
          return '#0ca678'
        case 'fair':
          return '#f59f00'
        case 'poor':
          return '#f03e3e'
        default:
          return '#0ca678'
      }
    },
    connectionSubtitle() {
      return ['fair', 'poor'].includes(this.qualityLevel)
        ? 'You may experience audio or video issues.'
        : 'Your audio and video should be working fine.'
    },
    metricsDefinitions() {
      const definitions = {
        latency: {
          label: 'Latency (RTT)',
          description: 'Round trip time for data to travel between participants',
          unit: 'ms'
        },
        jitter: {
          label: 'Jitter',
          description: 'Variance or fluctuation in latency',
          unit: 'ms'
        },
        packetLoss: {
          label: 'Packet loss',
          description: 'Amount of data lost during transfer',
          unit: '%'
        }
      }
      if (this.isWifiNetworkInterface) {
        definitions.wifiState = {
          label: 'Video Camera Wifi Strength',
          description: 'The strength of the WiFi signal from the video camera',
          unit: ''
        }
      }
      return definitions
    }
  },
  methods: {
    reset() {
      this.score = 1
      this.stats = {}
      this.issues = []
      if (this.issuesClearTimer) {
        clearTimeout(this.issuesClearTimer)
        this.issuesClearTimer = null
      }
    },
    toggleStats() {
      this.networkStatus = this.getNetworkStatus()
      this.statsModalOpen = true
    },
    getMetricColor(status) {
      switch (status) {
        case 'Good': return '#0ca678'
        case 'Fair': return '#f59f00'
        case 'Poor': return '#f03e3e'
        default: return '#0ca678'
      }
    },
    getStatusFromRange(value, ranges) {
      if (value <= ranges.good) return 'Good'
      if (value <= ranges.fair) return 'Fair'
      return 'Poor'
    },
    getNetworkStatus() {
      const issue = this.issues && this.issues[0]
      const issueSample = issue?.statsSample || {}

      let rtt = this.stats.rtt || 0
      let jitter = this.stats.avgJitter || 0
      let packetLoss = this.stats.packetsLoss || 0
      let speakerDelay = this.stats.avgJitterBufferDelay || issueSample.avgJitterBufferDelay || 0

      const jitterMs = Math.round(jitter * 1000)

      return {
        latency: {
          value: rtt,
          // Latency has the lowest weight in MOS calculation (0.024)
          // This means it takes a much higher latency to significantly impact the score
          status: this.getStatusFromRange(rtt, { good: 350, fair: 700 })
        },
        jitter: {
          value: jitterMs,
          // Jitter has a medium weight in MOS calculation (0.11)
          // This means it has a moderate impact on the final score
          status: this.getStatusFromRange(jitterMs, { good: 50, fair: 100 })
        },
        packetLoss: {
          value: packetLoss,
          // Packet loss has the highest weight in MOS calculation (2.5)
          // This means even small amounts of packet loss significantly impact the score
          status: this.getStatusFromRange(packetLoss, { good: 1, fair: 3 })
        },
        speakerDelay: {
          value: Math.round(speakerDelay),
          // Speaker delay is related to jitter and has similar impact on user experience
          // Using the same thresholds as jitter since they affect the experience similarly
          status: this.getStatusFromRange(speakerDelay, { good: 100, fair: 200 })
        },
        wifiState: {
          value: this.wifi.strength,
          status: this.wifi.state == 'warning' ? 'Poor' : 'Good'
        }
      }
    },
    updateNetworkStatus() {
      if (this.statsModalOpen) {
        this.networkStatus = this.getNetworkStatus()
      }
    }
  },
  watch: {
    issues: {
      handler(newIssues) {
        if (this.issuesClearTimer) {
          clearTimeout(this.issuesClearTimer)
          this.issuesClearTimer = null
        }

        if (newIssues.length > 0 && this.showPulsingBorder === false) {
          this.showPulsingBorder = true;
          setTimeout(() => {
            this.showPulsingBorder = false;
          }, 10000);
        }

        // issues aren't cleared by webrtc-issue-detector
        // so we need to clear them manually
        this.issuesClearTimer = setTimeout(() => {
          this.issues = []
          this.issuesClearTimer = null
        }, 4000)
        this.updateNetworkStatus()
      },
      immediate: true
    },
    stats: {
      handler() {
        this.updateNetworkStatus()
      },
      deep: true
    },
    score() {
      this.updateNetworkStatus()
    }
  }
}
</script>

<style scoped lang="scss">
.quality-indicator {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 19;
  background: rgba(0, 0, 0, 0.5);
  padding: 5px;
  border-radius: 4px;
  border: 2px solid transparent;
  cursor: pointer;

  &.is-video-meet {
    position: static;
    background: transparent;
    border: none;
    .signal-bars .bar {
      background: #b6b6b6;
    }
  }

  &.pulsing-border {
    animation: pulse-border 2.5s ease-in-out infinite;
  }

  .signal-bars {
    display: flex;
    align-items: flex-end;
    height: 15px;
    gap: 2px;

    .bar {
      width: 3px;
      background: rgba(255, 255, 255, 0.3);
      border-radius: 1px;

      &:nth-child(1) { height: 25%; }
      &:nth-child(2) { height: 50%; }
      &:nth-child(3) { height: 75%; }
      &:nth-child(4) { height: 100%; }

      &.active {
        background: #48c78e;
      }
    }
  }
}

.connection-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  .title {
    font-size: 16px;
    font-weight: 500;
  }
  .subtitle {
    font-size: 14px;
  }
}

.network-status {
  .metrics-grid {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
}

.metric-item {
  display: flex;
  justify-content: space-between;
  padding-bottom: 14px;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .metric-info {
    flex: 1;
  }

  .metric-value {
    text-align: right;
  }

  .label {
    font-weight: 600;
  }

  .description {
    font-size: 13px;
  }

  .status {
    font-weight: 500;
    margin-bottom: 8px;
  }

  .value {
    font-size: 13px;
  }
}

.advanced-details {
  margin-top: 15px;
  font-size: 12px;

  summary {
    cursor: pointer;
  }

  .json-data {
    background: #f5f5f5;
    border-radius: 4px;
    padding: 8px;
    max-height: 150px;
    overflow: auto;
    font-size: 11px;
    white-space: pre-wrap;
  }
}

@keyframes pulse-border {
  0% {
    border-color: rgba(255, 0, 0, 0.2);
  }
  50% {
    border-color: rgba(255, 0, 0, 0.6);
  }
  100% {
    border-color: rgba(255, 0, 0, 0.2);
  }
}
</style>
